/**
 * !!!
 * This is just a scaffold and loose suggestions; the needs of the actual solution
 * should dictate the final shape of the schemas.
 *
 * Leave as much power on TIC's side as reasonable to "puppeteer" Timely's side.
 */

import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';

extendZodWithOpenApi(z);

import { IdentityReadSchema } from '../integrations/integration.models.identity';
import {
  DateTimeTzSchema,
  GenericAccountId,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericToolId,
  GenericUserID,
  GenericUUID,
} from '../models.generic';
import { TimelyAllowedSideEffectsSchema } from './models.timely.side_effects';

/**
 * `resource.type`'s that Timely can understand
 * @ref provider.models.resource.ts, should probably line up with that.
 */
export const timelySupportedResourceTypeEnums = [
  'project',
  'task',
  'label',
  'tag',
  'team', // NOT YET SUPPORTED on Timely's side
  'client',
  'company',
  'organization',
  'identity',
] as const;
export const TimelySupportedResourceTypeEnumsSchema = z.enum(timelySupportedResourceTypeEnums);

/**
 * Timely's supported resource types.
 */
const TimelyResourceTypesEnums = [
  'project',
  'forecast',
  'identity',
  'label',
  'client',
  'team',
] as const;
export const TimelyResourceTypeEnumsSchema = z.enum(TimelyResourceTypesEnums);

/**
 * Per-resource sync options for Timely handlers.
 * All fields optional - only relevant fields populated per resource type.
 */
export const ResourceSyncOptionsSchema = z
  .object({
    /** Projects: add all Timely account users */
    add_all_workspace_users: z
      .boolean()
      .optional()
      .describe('Projects: add all Timely account users'),

    /** Projects: replace vs merge user list */
    replace_users_on_update: z
      .boolean()
      .optional()
      .describe('Projects: replace vs merge user list'),

    /** Projects: email invites for new users */
    send_invites: z.boolean().optional().describe('Projects: email invites for new users'),

    /** Projects: enforce required_labels flag */
    require_labels: z.boolean().optional().describe('Projects: enforce required_labels flag'),

    /** Projects: label visibility policy (all = show all workspace tags) */
    enable_labels: z
      .enum(['all', 'none', 'custom'])
      .optional()
      .describe('Projects: label visibility policy (all = show all workspace tags)'),

    /** Projects: billing rate type on creation */
    default_rate_type: z
      .enum(['project', 'user', 'non-billable'])
      .optional()
      .describe('Projects: billing rate type on creation'),

    /** Forecasts: auto-add task assignees to project */
    auto_add_assignees_to_projects: z
      .boolean()
      .optional()
      .describe(
        'Forecasts: when importing tasks with assignees that are not members of the project, auto-add them to the project before importing the task.'
      ),

    /** Side effects this resource is explicitly allowed to trigger in Timely */
    allowed_side_effects: TimelyAllowedSideEffectsSchema.optional().describe(
      'Side effects this resource is explicitly allowed to trigger in Timely'
    ),
  })
  .optional()
  .describe('Per-resource sync guidance for Timely');

export type ResourceSyncOptions = z.infer<typeof ResourceSyncOptionsSchema>;

/**
 * Timely's base/shared-across-all import model
 */
export const TimelyResourceBaseSchema = z.object({
  type: TimelyResourceTypeEnumsSchema,
  url: z.string().url().nullable().describe('The URL of the resource, if known'),
  tic_id: GenericUUID.describe('The TIC `id` reference to the resource'),
  uri: z
    .string()
    .nullable()
    .default(null)
    .describe('The TIC URI of the resource; prefer this to `tic_id` when possible'),
  timely_id: z
    .number()
    .int()
    .nullable()
    .default(null)
    .openapi({ description: 'The Timely ID of the resource, if known', example: null }),
  timely_account_id: GenericTimelyAccountId,
  tic_account_id: GenericAccountId.describe('The TIC `account.id` this resource belongs to'),
  // TODO: Implement this if possible (may)
  // creator_timely_user_id: GenericTimelyUserId.nullable().describe(
  //   '(Not yet supported) Timely user.id who should be the "creator" of this resource in Timely'
  // ),
  creator_tic_user_id: GenericUUID.describe('The TIC user ID of the creator').nullable(),
  external_id: z
    .string()
    .openapi({ description: 'The external ID of the resource', example: '123' }),
  external_tool_id: GenericToolId.openapi({ description: 'The TIC `tool.id` driving the import' }),
  updated_at: DateTimeTzSchema.describe(
    "When this resource was last updated. WARNING: CURRENTLY, this does not _necessarily_ reflect the external source's last update, but that is the intent."
  ),
  sync_options: ResourceSyncOptionsSchema,
});

/**
 * Timely's user<->project/task association import model
 */
export type TimelyResourceUserAssociation = z.infer<typeof TimelyResourceUserAssociationSchema>;
export const TimelyResourceUserAssociationSchema = z.object({
  // We don't have this available via `query_timely_importable_data_diff` RPC atm.
  // identity_id: GenericUUID.describe(
  //   'The TIC `identity.id` may not always belong to a Timely or TIC `user.id`'
  // ).nullable(),
  identity_uri: IdentityReadSchema.shape.uri,
  tic_identity_id: IdentityReadSchema.shape.id
    .nullable()
    .describe(
      'The TIC identity.id _SHOULDNT_ be null, likely local seeding setup issue, so allowing through.'
    ),
  tic_user_id: GenericUUID.describe(
    'The TIC `user/profile.id` reference to the user, if known'
  ).nullable(),
  // We don't have this available via `query_timely_importable_data_diff` RPC atm.
  // timely_user_id: GenericTimelyUserId.nullable()
  //   .describe('The Timely `user.id` reference to the user, if known')
  //   .nullable(),
  updated_at: DateTimeTzSchema.describe('The date of last update of this association'),
});

/**
 * Timely's label<->project/task association import model
 */
export const TimelyResourceLabelAssociationSchema = z.object({
  /**
   * All properties are optional because diffs may not always include the tag resource that
   * originally defined the association. Providers are expected to supply at least `external_id`
   * and `uri`, while the transformer enriches `tic_id` when the tag travels alongside the row.
   */
  /**
   * TIC resource identifier of the label when we know it. Downstream processors can
   * use this to link back to the exact tag row we just exported.
   */
  tic_id: GenericUUID.describe('The TIC `resource.id` reference to the label').optional(),
  /**
   * Fallback identifier from the provider – enables Timely to match previously seen
   * labels even if this diff does not include the tag resource itself.
   */
  external_id: z
    .string()
    .describe('The provider label identifier, used as a fallback when tic_id is unavailable')
    .optional(),
  /**
   * Canonical URI for the label inside TIC. Including this allows downstream code to
   * look up the label even when neither tic_id nor external_id are present.
   */
  uri: z
    .string()
    .describe('The TIC URI reference to the label, useful for cross-diff reconciliation')
    .optional(),
  /** Human-readable label title when providers share it. */
  title: z.string().nullable().optional(),
});

const TimelyResourceLabelsArraySchema = z
  .array(TimelyResourceLabelAssociationSchema)
  .default([])
  .describe(
    'Label associations attached to the resource. Defaults to an empty list. These labels are attached directly on the resource metadata and do not have DB-relationships'
  );

/**
 * Timely's Label/Tag import model
 */
export type TimelyLabelFromResource = z.infer<typeof TimelyLabelFromResourceSchema>;
export const TimelyLabelFromResourceSchema = TimelyResourceBaseSchema.extend({
  type: z.literal('label'),
  name: z.string().openapi({ description: 'The label name', example: 'General Work' }),
  parent_label: z.object({
    uri: z.string().nullable().default(null).openapi({
      description: 'The TIC URI of the parent label (preferred identifier)',
      example: 'tic://linear/default/tags/parent-label',
    }),
    tic_id: GenericUUID.nullable().default(null).openapi({
      description: 'The TIC resource ID of the parent label',
      example: null,
    }),
    external_id: z.string().nullable().default(null).openapi({
      description: 'The external ID of the parent label (fallback identifier)',
      example: null,
    }),
  }),
});

/**
 * Timely's Project import model
 */
export type TimelyProjectFromResource = z.infer<typeof TimelyProjectFromResourceSchema>;
export const TimelyProjectFromResourceSchema = TimelyResourceBaseSchema.extend({
  type: z.literal('project'),
  name: z.string().openapi({ description: 'The project name', example: 'Project A' }),
  description: z
    .string()
    .nullable()
    .default(null)
    .describe('The project description, if available'),
  color: z.string().nullable().default(null).describe('The project color, if known'),
  // enable_labels: z.enum(['all', 'none', 'custom']).describe('Enable labels for this project'),
  active: z.boolean().describe('Is active? Turn false to archive project automatically'),
  company: z
    .object({
      tic_id: GenericUUID.nullable().describe(
        'The TIC `resource.id` reference to the company ("client")'
      ),
      timely_id: z.number().int().nullable().openapi({
        example: 1,
        description: 'The Timely ID of the company ("client"), if known',
      }),
      fallback_timely_id: z
        .number()
        .nullable()
        .default(null)
        .describe('The fallback Timely `client_id` to use if `timely_id` is not supplied'),
      fallback_title: z
        .string()
        .nullable()
        .default(null)
        .describe(
          "The project's parent `resource.title`, useful for assigning to existing clients with same name on Timely side. Beware the potential instability of this (e.g parent is renamed externally)"
        ),
    })
    .nullable()
    .describe('References to the company (aka "client") for this project'),
  users: z.array(TimelyResourceUserAssociationSchema).nullable(),
  labels: TimelyResourceLabelsArraySchema,
});

/**
 * Timely's Client/Company import model
 */
export type TimelyClientFromResource = z.infer<typeof TimelyClientFromResourceSchema>;
export const TimelyClientFromResourceSchema = TimelyResourceBaseSchema.extend({
  // tic_id === project.company_id
  type: z.literal('client'),
  name: z.string().openapi({ description: 'The client name', example: 'Client A' }),
  active: z.boolean().describe('Is active?. Set false to archive client'),
});

/**
 * Timely's Team import model
 */
export type TimelyTeamFromResource = z.infer<typeof TimelyTeamFromResourceSchema>;
export const TimelyTeamFromResourceSchema = TimelyResourceBaseSchema.extend({
  type: z.literal('team'),
  name: z.string().openapi({ description: 'The team name', example: 'Engineering' }),
  description: z.string().optional(),
  active: z.boolean().describe('Is active? Set false to archive team'),
  users: z.array(TimelyResourceUserAssociationSchema),
});

/**
 * Timely's Task/Forecast import model
 * @todo Probably want to tighten the optional/nullability here,
 *       and exclude anything that doesn't parse from the diff output.
 */
export type TimelyForecastFromResource = z.infer<typeof TimelyForecastFromResourceSchema>;
export const TimelyForecastFromResourceSchema = TimelyResourceBaseSchema.extend({
  type: z.literal('forecast'),
  title: z.string().openapi({ description: 'The forecast title', example: 'Some task title' }),
  description: z
    .string()
    .openapi({ description: 'The forecast description', example: 'Do X, Y, and Z...' }),
  from: DateTimeTzSchema.describe('The forecast start date').nullable(), // TODO proper
  to: DateTimeTzSchema.describe('The forecast end date').nullable(), // TODO proper
  estimated_minutes: z.number().default(0).describe('The estimated minutes of the forecast'),
  completed_at: DateTimeTzSchema.nullable().describe('The completed date of the forecast'),
  users: z.array(TimelyResourceUserAssociationSchema),
  labels: TimelyResourceLabelsArraySchema,
  project: z.object({
    tic_id: GenericUUID.describe('The TIC `resource.id` reference of project').nullable(),
    /**
     * TIC synthetic URI for the project. Enables reliable project resolution
     * when tic_id is not available (e.g., project not in current diff).
     */
    uri: z
      .string()
      .nullable()
      .optional()
      .describe('The TIC URI reference to the project, for cross-diff reconciliation'),
    /**
     * Provider's external ID for the project. Final fallback when neither
     * tic_id nor uri resolve to a known project.
     */
    external_id: z
      .string()
      .nullable()
      .optional()
      .describe('The provider project identifier, used as a last-resort fallback'),
  }),
  // TODO: A way to delete a forecast?
  // active: z.boolean().describe('Is active?. Set false to archive forecast'), // ??
});

/**
 * TIC's known identities, to be used in Timely for user creation.
 */
export type TimelyIdentity = z.infer<typeof TimelyIdentitySchema>;
export const TimelyIdentitySchema = z.object({
  type: z.literal('identity'),
  tic_id: IdentityReadSchema.shape.id,
  tic_user_id: GenericUserID.nullable().describe(
    'The TIC `user.id` associate to this identity, if known'
  ),
  timely_user_id: GenericTimelyUserId.nullable().describe(
    'The Timely `user.id` reference to the user, if known'
  ),
  timely_account_id: GenericTimelyAccountId,
  uri: IdentityReadSchema.shape.uri,
  external_id: IdentityReadSchema.shape.external_user_id,
  external_email: IdentityReadSchema.shape.external_email,
  external_name: IdentityReadSchema.shape.external_name,
  external_tool_id: IdentityReadSchema.shape.tool_id,
  external_timezone: IdentityReadSchema.shape.external_timezone,
  metadata: IdentityReadSchema.shape.metadata,
});

/**
 * Union of all resources that Timely can import.
 */
export type AnyTimelyResource = z.infer<typeof AnyTimelyResourceSchema>;
export const AnyTimelyResourceSchema = z.union([
  TimelyProjectFromResourceSchema,
  TimelyForecastFromResourceSchema,
  TimelyLabelFromResourceSchema,
  TimelyClientFromResourceSchema,
  TimelyTeamFromResourceSchema,
]);

/**
 * Timely's preferred envelope/structure for importing resources.
 */
export const timelyResourceDataKeysEnum = [
  'identities',
  'labels',
  'companies',
  'projects',
  'tasks',
  'teams',
] as const;
export type TimelyResourcesData = z.infer<typeof TimelyResourcesDataSchema>;
export const TimelyResourcesDataSchema = z.object({
  identities: z.array(TimelyIdentitySchema),
  labels: z.array(TimelyLabelFromResourceSchema),
  companies: z.array(TimelyClientFromResourceSchema),
  projects: z.array(TimelyProjectFromResourceSchema),
  tasks: z.array(TimelyForecastFromResourceSchema),
  teams: z.array(TimelyTeamFromResourceSchema),
});
